import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Trash2,
  Edit2,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  HardDrive,
  Image,
  X,
  Folder,
  Plus,
  ChevronRight,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useAssets,
  useAssetFolderCounts,
  useStorageMetrics,
  useUpdateAsset,
  useDeleteAsset,
  usePurgeAssets,
  useAssetFolders,
  useCreateFolder,
  useDeleteFolder,
  STORAGE_QUOTA_BYTES,
  type Asset,
  type AssetFilter,
  type AssetFolder,
} from '@/hooks/use-assets'
import MediaPicker from './media-picker'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

// ── Storage Quota Bar ─────────────────────────────────────────────────────────

function StorageQuotaBar({ bytesUsed, quota }: { bytesUsed: number; quota: number }) {
  const percent = Math.min(100, (bytesUsed / quota) * 100)
  const isNearFull = percent >= 80
  const isFull = percent >= 100

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card">
      <HardDrive
        className={cn(
          'h-5 w-5 shrink-0',
          isFull ? 'text-destructive' : isNearFull ? 'text-yellow-500' : 'text-muted-foreground',
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium">Storage Quota</span>
          <span
            className={cn(
              'text-sm font-mono',
              isFull ? 'text-destructive' : isNearFull ? 'text-yellow-500' : 'text-muted-foreground',
            )}
          >
            {formatBytes(bytesUsed)} / {formatBytes(quota)} Used
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              isFull ? 'bg-destructive' : isNearFull ? 'bg-yellow-500' : 'bg-primary',
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
        {isFull && (
          <p className="text-xs text-destructive mt-1">
            Storage penuh. Hapus aset yang tidak terpakai untuk mengosongkan ruang.
          </p>
        )}
      </div>
    </div>
  )
}

// ── Usage Badge ───────────────────────────────────────────────────────────────

function UsageBadge({ usageCount }: { usageCount: number }) {
  if (usageCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-600 border border-green-500/30">
        <CheckCircle2 className="h-3 w-3" />
        In Use ({usageCount})
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-600 border border-yellow-500/30">
      <AlertCircle className="h-3 w-3" />
      Orphaned
    </span>
  )
}

// ── Folder Card ───────────────────────────────────────────────────────────────

interface FolderCardProps {
  folder: AssetFolder
  fileCount: number
  onClick: () => void
  onDelete: () => void
  isDeleting: boolean
}

function FolderCard({ folder, fileCount, onClick, onDelete, isDeleting }: FolderCardProps) {
  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (!window.confirm(`Hapus folder "${folder.name}"?\nAset di dalamnya akan dipindahkan ke root.`)) return
    onDelete()
  }

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-card hover:bg-accent cursor-pointer transition-all select-none"
    >
      <div className="shrink-0 h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
        <Folder className="h-5 w-5 text-primary/80" />
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-sm font-medium truncate">{folder.name}</span>
        <span className="shrink-0 text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5 leading-none tabular-nums">
          {fileCount}
        </span>
      </div>
      <button
        type="button"
        onClick={handleDeleteClick}
        disabled={isDeleting}
        title="Hapus folder"
        className="shrink-0 opacity-0 group-hover:opacity-100 h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all disabled:opacity-30"
      >
        {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

// ── New Folder Card ───────────────────────────────────────────────────────────
// Dashed card — clicking transforms it into an inline input form.

function NewFolderCard() {
  const createFolder = useCreateFolder()
  const [active, setActive] = useState(false)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (active) inputRef.current?.focus()
  }, [active])

  async function handleCreate() {
    if (!name.trim()) return
    setError(null)
    try {
      await createFolder.mutateAsync(name.trim())
      setName('')
      setActive(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal membuat folder')
    }
  }

  function handleCancel() {
    setActive(false)
    setName('')
    setError(null)
  }

  if (!active) {
    return (
      <button
        type="button"
        onClick={() => setActive(true)}
        className="group flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 text-muted-foreground hover:text-primary transition-all"
      >
        <div className="shrink-0 h-9 w-9 rounded-lg bg-muted group-hover:bg-primary/10 flex items-center justify-center transition-colors">
          <Plus className="h-5 w-5" />
        </div>
        <span className="text-sm font-medium">Buat Folder Baru</span>
      </button>
    )
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-primary/50 bg-primary/5 ring-1 ring-primary/20">
      <div className="shrink-0 h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
        <Folder className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <input
          ref={inputRef}
          value={name}
          onChange={(e) => { setName(e.target.value); setError(null) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate()
            if (e.key === 'Escape') handleCancel()
          }}
          placeholder="Nama folder"
          maxLength={60}
          className="w-full bg-transparent text-sm font-medium outline-none placeholder:text-muted-foreground/60"
        />
        {error && <p className="text-xs text-destructive mt-0.5">{error}</p>}
      </div>
      <button
        type="button"
        onClick={handleCreate}
        disabled={!name.trim() || createFolder.isPending}
        title="Buat"
        className="shrink-0 h-7 w-7 flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {createFolder.isPending
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Check className="h-3.5 w-3.5" />}
      </button>
      <button
        type="button"
        onClick={handleCancel}
        title="Batal"
        className="shrink-0 h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ── Edit Metadata Modal ───────────────────────────────────────────────────────

interface EditModalProps {
  asset: Asset | null
  folders: AssetFolder[]
  onClose: () => void
}

function EditMetadataModal({ asset, folders, onClose }: EditModalProps) {
  const updateAsset = useUpdateAsset()
  const [altText, setAltText] = useState(asset?.alt_text ?? '')
  const [caption, setCaption] = useState(asset?.caption ?? '')
  const [tags, setTags] = useState(asset?.tags.join(', ') ?? '')
  const [folderId, setFolderId] = useState<string | null>(asset?.folder_id ?? null)

  useEffect(() => {
    setAltText(asset?.alt_text ?? '')
    setCaption(asset?.caption ?? '')
    setTags(asset?.tags.join(', ') ?? '')
    setFolderId(asset?.folder_id ?? null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id])

  if (!asset) return null

  async function handleSave() {
    if (!asset) return
    const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean)
    await updateAsset.mutateAsync({
      id: asset.id,
      alt_text: altText.trim() || null,
      caption: caption.trim() || null,
      tags: tagList,
      folder_id: folderId,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="font-semibold">Edit Metadata Aset</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="h-32 w-full rounded-lg overflow-hidden border border-border bg-muted">
            <img src={asset.file_url} alt={asset.alt_text ?? asset.file_name} className="h-full w-full object-contain" />
          </div>
          <p className="text-xs text-muted-foreground truncate">{asset.file_name}</p>

          <div>
            <label className="block text-sm font-medium mb-1">Folder</label>
            <select
              value={folderId ?? ''}
              onChange={(e) => setFolderId(e.target.value || null)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="">— Tanpa Folder (Root) —</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Alt Text</label>
            <input type="text" value={altText} onChange={(e) => setAltText(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Caption</label>
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Tags <span className="text-muted-foreground font-normal">(pisahkan dengan koma)</span>
            </label>
            <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>

          {updateAsset.error && (
            <p className="text-sm text-destructive">Gagal menyimpan: {updateAsset.error.message}</p>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-accent transition-colors">
            Batal
          </button>
          <button type="button" disabled={updateAsset.isPending} onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
            {updateAsset.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Simpan
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Asset Card ────────────────────────────────────────────────────────────────

interface AssetCardProps {
  asset: Asset
  folderName?: string
  onEdit: (asset: Asset) => void
  onDelete: (id: string) => void
  isDeleting: boolean
}

function AssetCard({ asset, folderName, onEdit, onDelete, isDeleting }: AssetCardProps) {
  function handleDelete() {
    if (!window.confirm(`Hapus "${asset.file_name}"? Tindakan ini tidak dapat dibatalkan.`)) return
    onDelete(asset.id)
  }

  return (
    <div className="group relative rounded-xl overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
      <div className="aspect-square overflow-hidden bg-muted">
        <img
          src={asset.file_url}
          alt={asset.alt_text ?? asset.file_name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      {/* Overlay actions */}
      <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button type="button" onClick={() => onEdit(asset)} title="Edit metadata"
          className="h-7 w-7 flex items-center justify-center rounded-md bg-white/90 text-gray-700 hover:bg-white shadow-sm">
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={handleDelete} disabled={isDeleting} title="Hapus aset"
          className="h-7 w-7 flex items-center justify-center rounded-md bg-destructive/90 text-white hover:bg-destructive shadow-sm disabled:opacity-50">
          {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="p-3 space-y-1.5">
        <UsageBadge usageCount={asset.usage_count} />
        <p className="text-sm font-medium truncate leading-none mt-1">{asset.file_name}</p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{formatBytes(asset.file_size)}</span>
          <span>{asset.mime_type.split('/')[1].toUpperCase()}</span>
        </div>
        {folderName && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Folder className="h-3 w-3" />
            <span className="truncate">{folderName}</span>
          </div>
        )}
        {asset.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {asset.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded text-xs bg-muted text-muted-foreground">{tag}</span>
            ))}
            {asset.tags.length > 3 && (
              <span className="text-xs text-muted-foreground">+{asset.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── AssetManagerHub ───────────────────────────────────────────────────────────

type FilterTab = 'all' | 'in-use' | 'orphaned'

const filterTabs: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'Semua' },
  { key: 'in-use', label: 'In Use' },
  { key: 'orphaned', label: 'Orphaned' },
]

export default function AssetManagerHub() {
  const navigate = useNavigate()
  const { folderSlug } = useParams<{ folderSlug?: string }>()

  const [filterTab, setFilterTab] = useState<FilterTab>('all')
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null)
  const [purgeResult, setPurgeResult] = useState<string | null>(null)

  const assetFilter: AssetFilter =
    filterTab === 'in-use' ? 'in-use' : filterTab === 'orphaned' ? 'orphaned' : undefined

  const { data: folders = [], isLoading: foldersLoading } = useAssetFolders()

  // Derive currentFolderId from the URL slug — resolved against loaded folders
  const currentFolderId = useMemo<string | null>(() => {
    if (!folderSlug) return null
    const decoded = decodeURIComponent(folderSlug)
    return folders.find((f) => f.name.toLowerCase() === decoded.toLowerCase())?.id ?? null
  }, [folderSlug, folders])

  // Current view assets — scoped to current folder (null = root-only files)
  const { data: assets, isLoading: assetsLoading } = useAssets(assetFilter, currentFolderId)
  // Global orphaned count for purge button (all folders)
  const { data: allOrphaned } = useAssets('orphaned', undefined)
  // Per-folder file counts — uses a dedicated hook with a unique query key
  const { data: folderCountAssets = [] } = useAssetFolderCounts()
  const { data: storage, isLoading: storageLoading } = useStorageMetrics()

  const deleteAsset = useDeleteAsset()
  const deleteFolder = useDeleteFolder()
  const purgeAssets = usePurgeAssets()

  const currentFolder = folders.find((f) => f.id === currentFolderId) ?? null
  // Used to show folder name badge on file cards in root view
  const folderMap = new Map<string, string>(folders.map((f) => [f.id, f.name]))
  // Count of files per folder
  const folderFileCountMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const asset of folderCountAssets) {
      map.set(asset.folder_id, (map.get(asset.folder_id) ?? 0) + 1)
    }
    return map
  }, [folderCountAssets])

  async function handleDelete(id: string) {
    setDeletingId(id)
    try { await deleteAsset.mutateAsync(id) }
    finally { setDeletingId(null) }
  }

  async function handleDeleteFolder(id: string) {
    setDeletingFolderId(id)
    try {
      await deleteFolder.mutateAsync(id)
      if (currentFolderId === id) navigate('/media')
    } finally {
      setDeletingFolderId(null)
    }
  }

  async function handlePurge() {
    if (!window.confirm('Hapus semua aset Orphaned (tidak terpakai)? Tindakan ini tidak dapat dibatalkan dan akan menghapus file dari ImageKit.')) return
    setPurgeResult(null)
    try {
      const result = await purgeAssets.mutateAsync()
      setPurgeResult(
        result.deleted === 0
          ? 'Tidak ada aset orphaned yang ditemukan.'
          : `${result.deleted} aset berhasil dihapus.`,
      )
    } catch (err) {
      setPurgeResult(`Gagal: ${err instanceof Error ? err.message : 'Error tidak diketahui'}`)
    }
  }

  const orphanedCount = allOrphaned?.length ?? 0
  // Upload goes to current folder (null = root → undefined = no folder_id)
  const uploadFolderId = currentFolderId ?? undefined

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Media Library</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Kelola semua aset gambar untuk tenant ini.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={purgeAssets.isPending || orphanedCount === 0}
            onClick={handlePurge}
            title={orphanedCount === 0 ? 'Tidak ada aset orphaned' : `Hapus ${orphanedCount} aset orphaned`}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-destructive/50 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {purgeAssets.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Purge Unused
            {orphanedCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-xs bg-destructive/15 font-medium">
                {orphanedCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Upload Aset
          </button>
        </div>
      </div>

      {/* ── Storage Quota Bar ────────────────────────────────────────────────── */}
      {!storageLoading && storage && (
        <StorageQuotaBar bytesUsed={storage.bytes_used} quota={STORAGE_QUOTA_BYTES} />
      )}

      {/* ── Purge Result ─────────────────────────────────────────────────────── */}
      {purgeResult && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-muted border border-border text-sm">
          <span>{purgeResult}</span>
          <button type="button" onClick={() => setPurgeResult(null)} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Breadcrumb ───────────────────────────────────────────────────────── */}
      <nav className="flex items-center gap-1.5 text-sm">
        <button
          type="button"
          onClick={() => navigate('/media')}
          className={cn(
            'transition-colors',
            !currentFolderId
              ? 'text-foreground font-semibold pointer-events-none'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Media Library
        </button>
        {currentFolder && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex items-center gap-1.5 text-foreground font-semibold">
              <Folder className="h-3.5 w-3.5" />
              {currentFolder.name}
            </span>
          </>
        )}
      </nav>

      {/* ── Folders Section (root only) ──────────────────────────────────────── */}
      {currentFolderId === null && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
              Folder
            </h2>
            {folders.length > 0 && (
              <span className="text-xs text-muted-foreground">{folders.length}</span>
            )}
          </div>

          {foldersLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Memuat folder…</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {folders.map((folder) => (
                <FolderCard
                  key={folder.id}
                  folder={folder}
                  fileCount={folderFileCountMap.get(folder.id) ?? 0}
                  onClick={() => navigate('/media/' + encodeURIComponent(folder.name))}
                  onDelete={() => handleDeleteFolder(folder.id)}
                  isDeleting={deletingFolderId === folder.id}
                />
              ))}
              <NewFolderCard />
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-border" />
        </section>
      )}

      {/* ── Files Section ────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
            {currentFolder ? `File dalam "${currentFolder.name}"` : 'File'}
          </h2>
          {/* Filter tabs */}
          <div className="flex gap-0.5 p-1 rounded-lg bg-muted">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilterTab(tab.key)}
                className={cn(
                  'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                  filterTab === tab.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {assetsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (assets ?? []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
            <Image className="h-14 w-14 opacity-20" />
            <div className="text-center">
              <p className="text-sm font-medium">
                {filterTab === 'orphaned'
                  ? 'Tidak ada aset orphaned.'
                  : filterTab === 'in-use'
                  ? 'Tidak ada aset yang sedang digunakan.'
                  : currentFolder
                  ? `Folder "${currentFolder.name}" masih kosong.`
                  : 'Tidak ada file di root.'}
              </p>
              {filterTab === 'all' && !currentFolder && (
                <p className="text-xs mt-1">Klik "Upload Aset" atau pindahkan file dari folder.</p>
              )}
              {filterTab === 'all' && currentFolder && (
                <p className="text-xs mt-1">Klik "Upload Aset" untuk menambahkan file ke folder ini.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {(assets ?? []).map((asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                // Show folder badge only in root view for files that are in a folder
                folderName={
                  currentFolderId === null && asset.folder_id
                    ? (folderMap.get(asset.folder_id) ?? undefined)
                    : undefined
                }
                onEdit={setEditingAsset}
                onDelete={handleDelete}
                isDeleting={deletingId === asset.id}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Edit Metadata Modal ──────────────────────────────────────────────── */}
      <EditMetadataModal
        asset={editingAsset}
        folders={folders}
        onClose={() => setEditingAsset(null)}
      />

      {/* ── Upload via MediaPicker ───────────────────────────────────────────── */}
      <MediaPicker
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSelect={() => setUploadOpen(false)}
        uploadFolderId={uploadFolderId}
      />
    </div>
  )
}
